import { Button } from '@avanta/shared/components/ui/Button';
import { APPOINTMENT_REMOTE_ID, SPECIALITY_ICONS } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchSpecialitiesByPsycho } from '@avanta/shared/hooks/useFetchSpecialitiesByPsycho';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight, Mic, User } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AnimatedContainer } from 'components/ui/AnimatedContainer';
import { useSpecialityStepForm } from 'pages/Appointments/AppointmentsReserve/hooks/useSpecialityStepForm';
import { AppointmentType } from 'types/appointments';
import { CONTAINER_XL_MAX_WIDTH } from '../../../styles';
import {
  AppointmentsReservePayload,
  CONTACT_METHOD_ICONS,
  CONTACT_METHODS,
  type StepProps,
} from '../../../types';
import { BackButton } from '../../ui/BackButton';
import { CenterSection } from '../../ui/CenterSection';
import { CheckboxCard } from '../../ui/CheckboxCard';
import { SearchSummary } from '../../ui/SearchSummary';
import { StepSection } from '../../ui/StepSection';
import { SearchSpecialitySkeleton } from './SearchSpecialitySkeleton';
import { useGetSpecialityOptions } from './utils';

export const SearchSpecialityStep = ({ handleBack, handleNext }: StepProps): React.ReactElement => {
  const { watch, setValue, reset, getValues } = useFormContext();
  const [psychologist] = watch([AppointmentsReservePayload.psychologist]);
  const { data: bookingOptions, isLoading } = useFetchSpecialitiesByPsycho({
    psychologistId: psychologist?.id,
  });
  const citiesOptions = useGetSpecialityOptions(AppointmentsReservePayload.city);
  const centersOptions = useGetSpecialityOptions(AppointmentsReservePayload.backofficeCenterId);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const {
    specialityBooked,
    contactMethod,
    city,
    backofficeCenterId,
    isDisabled,
    handleOnCityChange,
    handleOnContactMethodChange,
  } = useSpecialityStepForm();

  const { specialities, workCenters } = bookingOptions ?? {};

  const hasRemoteWorkCenter = workCenters?.some(
    workCenter => String(workCenter.id) === String(APPOINTMENT_REMOTE_ID)
  );

  const hasInPersonWorkCenter = workCenters?.some(
    workCenter => String(workCenter.id) !== String(APPOINTMENT_REMOTE_ID)
  );

  const contactMethodOptions = ((): typeof CONTACT_METHODS => {
    if (hasRemoteWorkCenter && !hasInPersonWorkCenter) {
      return [AppointmentType.Video];
    }
    if (hasInPersonWorkCenter && !hasRemoteWorkCenter) {
      return [AppointmentType.InPerson];
    }
    return CONTACT_METHODS;
  })();

  const handleOnBack = (): void => {
    reset({
      ...getValues(),
      [AppointmentsReservePayload.specialityBooked]: undefined,
      [AppointmentsReservePayload.contactMethod]: undefined,
      [AppointmentsReservePayload.city]: undefined,
      [AppointmentsReservePayload.backofficeCenterId]: undefined,
    });
    handleBack?.();
  };

  return (
    <AnimatedContainer
      className={cn(CONTAINER_XL_MAX_WIDTH, 'flex flex-col h-full gap-8 am-lg:gap-10')}
    >
      <div className="flex am-lg:flex-row flex-col gap-6">
        <SearchSummary title={t('AppointmentsReserve.Search.Speciality.resume')} />
        {isLoading ? (
          <SearchSpecialitySkeleton />
        ) : (
          <div className="flex flex-col gap-8 am-lg:gap-10 w-full">
            <StepSection
              title={t('AppointmentsReserve.Steps.speciality.title')}
              className="grid grid-cols-2 gap-4 am-lg:flex"
            >
              {specialities?.map(speciality => (
                <CheckboxCard
                  key={speciality.id}
                  id={String(speciality.id)}
                  label={t(`AppointmentsReserve.Steps.speciality.options.${speciality.name}`)}
                  icon={SPECIALITY_ICONS[speciality.id as keyof typeof SPECIALITY_ICONS] ?? User}
                  checked={specialityBooked === speciality.id}
                  containerClassName="w-full h-20 am-lg:h-24"
                  onCheckedChange={() =>
                    setValue(AppointmentsReservePayload.specialityBooked, speciality.id)
                  }
                />
              ))}
            </StepSection>
            <StepSection title={t('AppointmentsReserve.Steps.type.title')} className="flex gap-4">
              {contactMethodOptions?.map(method => (
                <CheckboxCard
                  key={method}
                  id={method}
                  label={t(`AppointmentsReserves.Steps.type.options.${method}`)}
                  containerClassName="w-full"
                  checked={contactMethod === method}
                  icon={CONTACT_METHOD_ICONS[method as keyof typeof CONTACT_METHOD_ICONS] ?? Mic}
                  onCheckedChange={() => handleOnContactMethodChange(method)}
                />
              ))}
            </StepSection>
            {contactMethod === AppointmentType.InPerson && specialityBooked ? (
              <CenterSection
                citiesOptions={citiesOptions}
                centersOptions={centersOptions}
                city={city}
                backofficeCenterId={backofficeCenterId}
                onCityChange={handleOnCityChange}
              />
            ) : null}
          </div>
        )}
      </div>
      <div className="flex justify-between gap-4 pb-4">
        <BackButton handleBack={handleOnBack} />
        <Button
          type="button"
          variant="primary"
          className="flex-1 am-lg:w-fit am-lg:flex-none"
          size="lg"
          icon="trailing"
          onClick={handleNext}
          disabled={isDisabled}
        >
          {t('AppointmentsReserve.next')}
          <ChevronRight className="size-5" />
        </Button>
      </div>
    </AnimatedContainer>
  );
};
